/* eslint-disable react/no-array-index-key */
import { useState, useEffect, useRef, ChangeEvent } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthService } from 'services/auth.service';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { ROUTES } from 'utils/constants';
import { loginAction } from 'store/modules/auth/actions';
import { setMessageAction } from 'store/modules/helper/actions';
import { Box, TextField, Button, Typography, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Page from 'components/templates/Page';
import useTimer from 'hooks/useTimer';
import useCaptcha from 'hooks/useCaptcha';
import ScreenLoading from 'components/organisms/ScreenLoading';
import CaptchaTerms from 'components/atoms/CaptchaTerms';
import { useStyles } from './styles';

function AccessCode() {
  const styles = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const action = searchParams.get('action');
  const isCompany = action === 'job_open';

  const { message } = useAppSelector((state) => state.helper);
  const { formattedTime, start, pause, secondsAmount } = useTimer();
  const { captchaToken, executeRecaptcha } = useCaptcha();
  const captchaIsLoad = !!captchaToken;

  const [email, setEmail] = useState('');
  const [numbers, setNumbers] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoading, setFirstLoading] = useState(true);

  const isCodeFailed = message?.type === 'error';
  const hasError = secondsAmount === 0 || isCodeFailed;
  const codeErrorStatus = isCodeFailed ? 'está incorreto' : 'expirou';

  const inputRefs: any = useRef([]);
  const code = numbers.reduce(
    (accumulator, currentValue) => accumulator + currentValue
  );

  const generateCode = async (newEmail: string) => {
    setIsLoading(true);

    if (executeRecaptcha) {
      const newCaptchaToken = await executeRecaptcha('login');

      AuthService.codeGenerate(newEmail, isCompany, newCaptchaToken)
        .then(() => {
          dispatch(setMessageAction(null));
          start(120);
          setNumbers(['', '', '', '', '', '']);
        })
        .catch((error) => console.error(error))
        .finally(() => {
          setFirstLoading(false);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (captchaIsLoad) {
      if (location.state) {
        const { email: newEmail, generatedCode } = location.state;
        setEmail(newEmail);
        const newState = { ...location.state, email: newEmail };
        const newLocation = { ...location, state: newState };

        if (!generatedCode) {
          generateCode(newEmail);
        } else {
          navigate(newLocation.pathname + newLocation.search, {
            state: newLocation.state
          });
          setFirstLoading(false);
          start(120);
        }
      } else {
        navigate(`${ROUTES.AUTHENTICATION}${location.search}`);
      }
    }
  }, [captchaIsLoad]);

  const sendCode = async () => {
    let newCaptchaToken = captchaToken;

    if (executeRecaptcha) {
      newCaptchaToken = await executeRecaptcha('login');
    }
    dispatch(loginAction(email, code, newCaptchaToken));
  };

  useEffect(() => {
    if (code.length === 6 && captchaToken) {
      sendCode();
      pause();
    }
  }, [code]);

  const changeCode = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    index: number
  ) => {
    const { value } = event.target;

    setNumbers((state) => {
      const newState = [...state];
      newState[index] = value;

      return newState;
    });

    if (value && index < numbers.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const title = (
    <span>
      Digite o código
      <br />
      de acesso
    </span>
  );

  const subtitle = (
    <span>
      Insira o código de 6 dígitos que enviamos para{' '}
      <strong style={{ color: '#000000DE' }}>{email}</strong>
    </span>
  );

  const goToDidntReceiveCode = () => {
    navigate(`${ROUTES.DIDINT_RECEIVE_CODE}${location.search}`, {
      state: {
        email
      }
    });
  };

  const button = () =>
    !hasError ? (
      <Button
        onClick={goToDidntReceiveCode}
        className={styles.button}
        variant="text"
      >
        não recebi o código
      </Button>
    ) : (
      <LoadingButton
        loading={isLoading}
        disabled={isLoading}
        onClick={() => {
          generateCode(email).then(() => {});
        }}
        style={{ marginTop: 25 }}
      >
        reenviar código
      </LoadingButton>
    );

  if (firstLoading) {
    return <ScreenLoading />;
  }

  return (
    <Page title={title} subtitle={subtitle}>
      {hasError && (
        <Alert severity="warning" className={styles.error}>
          Seu código de autenticação <strong>{codeErrorStatus}</strong>. Por
          favor, tente novamente.
        </Alert>
      )}
      <Box className={styles.input} style={{ marginTop: !hasError ? 30 : 12 }}>
        {numbers.map((data, index) => (
          <TextField
            key={index}
            value={data}
            onChange={(e) => changeCode(e, index)}
            inputRef={(ref) => {
              inputRefs.current[index] = ref;
            }}
            inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
            style={{ width: 50 }}
            autoComplete="off"
            disabled={hasError}
          />
        ))}
      </Box>
      <CaptchaTerms />
      <Typography className={styles.info}>
        O código expira em: <span className={styles.time}>{formattedTime}</span>
      </Typography>
      {button()}
    </Page>
  );
}

export default AccessCode;
